import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function bubbleBase(role) {
  if (role === "user") {
    return "ml-auto bg-primary text-primary-foreground";
  }
  return "mr-auto bg-card/70 text-foreground border border-border/60";
}

export default function ExperienceChat({
  objective,
  scenario,
  useLlm,
  runId,
  setRunId,
}) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [lastIndicators, setLastIndicators] = useState({
    rehydrated: false,
    compressed: false,
    distilled: false,
  });

  const bottomRef = useRef(null);

  const canSend = Boolean(runId) && !loading;

  const ensureRun = async () => {
    if (runId) return runId;
    const res = await axios.post(`${API}/runs`, {
      objective,
      scenario,
      config: {
        use_llm: useLlm,
        stm_max_messages: 24,
        compression_token_threshold: 2400,
        compression_interval_steps: 3,
        llm_provider: "openai",
        llm_model: "gpt-5.2",
      },
    });
    setRunId(res.data.run_id);
    return res.data.run_id;
  };

  const refreshFromStm = async (rid) => {
    const memRes = await axios.get(`${API}/runs/${rid}/memory`);
    const stm = memRes.data?.stm || [];
    const view = stm
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        id: m.message_id,
        role: m.role,
        content: m.content,
        ts: m.ts,
      }));
    setMessages(view);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const rid = await ensureRun();
        await refreshFromStm(rid);
      } catch (e) {
        console.error(e);
        toast({
          title: "Failed to start session",
          description: e?.response?.data?.detail || e.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;

    setLoading(true);
    try {
      const rid = await ensureRun();

      setMessages((prev) => [
        ...prev,
        { id: `local_user_${Date.now()}`, role: "user", content: text },
      ]);
      setInput("");

      const stepRes = await axios.post(`${API}/runs/${rid}/step`, {
        user_message: text,
      });

      await refreshFromStm(rid);

      setLastIndicators({
        rehydrated: true,
        compressed: Boolean(stepRes.data?.triggered_compression),
        distilled: Boolean(stepRes.data?.triggered_compression),
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Message failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const headerBadges = useMemo(() => {
    const items = [];
    if (lastIndicators.distilled) items.push("Context distilled");
    if (lastIndicators.rehydrated) items.push("Relevant memory rehydrated");
    if (lastIndicators.compressed) items.push("Compression applied");
    return items;
  }, [lastIndicators]);

  return (
    <div data-testid="experience-mode" className="mx-auto max-w-5xl px-4 sm:px-6">
      <div className="pt-4 sm:pt-8 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div
              data-testid="experience-title"
              className="text-lg font-semibold tracking-tight"
            >
              Experience Mode
            </div>
            <div
              data-testid="experience-subtitle"
              className="text-sm text-muted-foreground"
            >
              Chat
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="indicators-toggle-button"
              variant="outline"
              onClick={() => setIndicatorsOpen((v) => !v)}
              className="rounded-full"
            >
              Indicators
            </Button>
          </div>
        </div>

        {indicatorsOpen ? (
          <Card
            data-testid="indicators-panel"
            className="mt-4 border-border/60 bg-card/60 backdrop-blur supports-[backdrop-filter]:bg-card/40"
          >
            <div className="p-4">
              <div
                data-testid="indicators-panel-title"
                className="text-sm font-medium"
              >
                Intelligence indicators
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="indicators-list">
                {headerBadges.length === 0 ? (
                  <div
                    data-testid="indicators-empty"
                    className="text-sm text-muted-foreground"
                  >
                    No indicators yet. Send a message.
                  </div>
                ) : (
                  headerBadges.map((t) => (
                    <Badge key={t} data-testid={`indicator-${t}`} variant="secondary">
                      {t}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </Card>
        ) : null}
      </div>

      <div
        data-testid="chat-surface"
        className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur supports-[backdrop-filter]:bg-card/30 overflow-hidden"
      >
        <ScrollArea
          data-testid="chat-scroll"
          className="h-[calc(100dvh-260px)] sm:h-[580px]"
        >
          <div className="p-6 space-y-4" data-testid="chat-message-list">
            {messages.length === 0 ? (
              <div
                data-testid="chat-empty"
                className="text-sm text-muted-foreground"
              >
                Start by asking something.
              </div>
            ) : null}

            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`chat-message-${m.id}`}
                className={m.role === "user" ? "flex" : "flex"}
              >
                <div
                  data-testid={`chat-bubble-${m.id}`}
                  className={`max-w-[90%] sm:max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${bubbleBase(
                    m.role
                  )}`}
                >
                  <div
                    data-testid={`chat-bubble-role-${m.id}`}
                    className="sr-only"
                  >
                    {m.role}
                  </div>
                  <div data-testid={`chat-bubble-content-${m.id}`}>{m.content}</div>
                </div>
              </div>
            ))}

            {loading ? (
              <div data-testid="chat-loading" className="flex">
                <div className="mr-auto rounded-2xl border border-border/60 bg-card/70 px-4 py-3 text-sm text-muted-foreground">
                  Working…
                </div>
              </div>
            ) : null}

            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <Separator />

        <div
          data-testid="chat-input-bar"
          className="p-4 bg-background/40"
        >
          <div className="flex gap-2 items-center">
            <Input
              data-testid="experience-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={loading ? "Working…" : "Message"}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <Button
              data-testid="experience-send-button"
              onClick={send}
              disabled={!canSend || !input.trim()}
              className="rounded-full"
            >
              Send
            </Button>
          </div>
          <div
            data-testid="experience-footnote"
            className="hidden"
          >
            
          </div>
        </div>
      </div>

      <div className="h-10" />
    </div>
  );
}
